// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/brand.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// brand.ts — the BRAND MARK per style: the browser-tab favicon, and the
// attention badge painted over it.
//
// Owner ruling 2026-09-05 (Styles × Themes §0): "the app logo mark and the
// favicon swap with the chosen style, applied per-user at runtime; the server
// default stays Blueprint." This module is that swap, and it is the ONE place
// that writes `<link id="favicon">.href` — a second writer is how a tab ends
// up showing the previous style's mark until the next attention flip.
//
// Everything is painted from the LIVE computed tokens at call time rather
// than from a file, for the same reason the Blueprint badge always was: a
// theme switch must move the mark without a media query and without a
// network fetch. The static files (public/favicon.svg, public/favicon-glass
// .svg) remain the pre-JS and hard-refresh fallback that index.html points
// at, and public/logo-glass.svg is the standalone asset for surfaces outside
// this bundle.
//
// ADDITIVE BY CONSTRUCTION: under the identity style every function here
// returns exactly what app.ts produced before PR-5 — the same helm glyph,
// the same badge geometry, the same '/favicon.svg' when nothing needs
// attention. The in-app mark needs no code at all: it is `icon('helm')`, and
// the style's icon SET (icons-glass.ts) answers that id with the iris.

import { currentStyle } from './style-axis.js';

/** Read a live custom property off <html>, with a literal fallback for the
 * pre-paint window where the token has not resolved yet. */
function token(name: string, fallback: string): string {
  const cs = getComputedStyle(document.documentElement);
  return cs.getPropertyValue(name).trim() || fallback;
}

function dataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const OPEN =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
  'stroke-linecap="round" stroke-linejoin="round">';

/** The attention badge — identical geometry in every style, because it is a
 * STATE signal and must be recognisable across a style switch. */
function badge(): string {
  const fill = token('--yellow-fill', '#FFD66B');
  const ink = token('--onyellow', '#1d2426');
  return `<circle cx="18.5" cy="5.5" r="5" fill="${fill}" stroke="${ink}" stroke-width="1.4"/>`;
}

/** BLUEPRINT — the rail's helm (Lucide ship-wheel), verbatim the mark this
 * app has always shown. */
function blueprintMark(attention: boolean): string {
  const accent = token('--accent', '#83BD4F');
  return (
    OPEN +
    `<g stroke="${accent}" stroke-width="2">` +
    '<circle cx="12" cy="12" r="8"/><path d="M12 2v7.5"/><path d="m19 5-5.23 5.23"/>' +
    '<path d="M22 12h-7.5"/><path d="m19 19-5.23-5.23"/><path d="M12 14.5V22"/>' +
    '<path d="M10.23 13.77 5 19"/><path d="M9.5 12H2"/><path d="M10.23 10.23 5 5"/>' +
    '<circle cx="12" cy="12" r="2.5"/>' +
    '</g>' +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** GLASS — the iris, at favicon detail: the aperture ring, the pupil, and the
 * spectral sliver. The sliver's three collinear segments are the theme's own
 * --accent / --sea / --aux-2, which is what makes the mark theme-tinted
 * without one baked hex (spec §7). Blades are dropped below 20 px — they
 * smear — so the favicon carries silhouette, not detail. */
function glassMark(attention: boolean): string {
  const accent = token('--accent', '#9B5CFF');
  const sea = token('--sea', '#5C9BFF');
  const aux2 = token('--aux-2', '#5CFFE0');
  return (
    OPEN +
    `<g stroke="${accent}" stroke-width="2">` +
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4"/>' +
    '</g>' +
    '<g stroke-width="2.2">' +
    `<path d="M4.6 15.4L9.53 13.13" stroke="${accent}"/>` +
    `<path d="M9.53 13.13L14.47 10.87" stroke="${sea}"/>` +
    `<path d="M14.47 10.87L19.4 8.6" stroke="${aux2}"/>` +
    '</g>' +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** SYNTHWAVE '88 — the sun on the horizon: a stroked wireframe RING-STACK
 * half-disc sitting on a single horizon rule that extends past it on both
 * sides. It is the background's own sun motif at icon scale, and it reads as
 * both "sunset" and "an instrument's horizon line" (spec §7).
 *
 * SILHOUETTE: half-disc on a rule — distinguishable at 16px from the other
 * six marks in the programme (circle / hexagon / squared roundel /
 * cross-with-a-dot / solid block / two bars), which is ruling R22.
 *
 * Single `currentColor`-style stroke in ONE hue: the chrome gradient is a
 * TEXT treatment and never touches the mark, so the mark stays legible at
 * 16px and re-tints per theme for free. Below ~20px the arcs collapse, so the
 * favicon carries silhouette rather than detail: the outer arc thickens and
 * the two inner rings drop out. */
function synthwaveMark(attention: boolean): string {
  const accent = token('--accent', '#FF2FA8');
  return (
    OPEN +
    `<g stroke="${accent}" stroke-linecap="butt" stroke-linejoin="miter">` +
    '<path d="M2 16h20" stroke-width="2"/>' +
    '<path d="M20.5 16a8.5 8.5 0 0 0-17 0" stroke-width="2.4"/>' +
    '<path d="M16.6 16a4.6 4.6 0 0 0-9.2 0" stroke-width="1.6"/>' +
    '<path d="M13.4 16a1.4 1.4 0 0 0-2.8 0" stroke-width="1.6"/>' +
    '</g>' +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** QUARTERDECK — the plotted fix: four asymmetric rhumb spikes meeting at a
 * centre dot, no ring. The N spike is longest, which is the whole silhouette
 * argument (a symmetric cross is a plus sign; an asymmetric one is a
 * compass — ruling R22). Painted in the live `--accent` with the N spike in
 * `--accent-2`, so the mark is brass on Dead Reckoning, verdigris on Storm
 * Glass and dark bronze on Chart Light without one baked hex (spec §7). The
 * four intercardinal half-rhumbs the standalone logo carries are dropped
 * here: at 16px they close the gaps and the rose becomes a grey asterisk. */
function quarterdeckMark(attention: boolean): string {
  const accent = token('--accent', '#C99A45');
  const accent2 = token('--accent-2', '#E2B662');
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
    'stroke-linecap="butt" stroke-linejoin="bevel">' +
    `<g stroke="${accent}" stroke-width="1.8">` +
    '<path d="M12 20.4 10.7 14.6 12 12 13.3 14.6Z"/>' +
    '<path d="M20.4 12 14.6 13.3 12 12 14.6 10.7Z"/>' +
    '<path d="M3.6 12 9.4 10.7 12 12 9.4 13.3Z"/>' +
    '<circle cx="12" cy="12" r="1.2"/>' +
    '</g>' +
    `<path d="M12 1.2 13.9 9.2 12 12 10.1 9.2Z" stroke="${accent2}" stroke-width="1.8"/>` +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** STARSHIP — the viewport: a hexagonal aperture with a single vertical
 * needle through its centre, and two bearing ticks. Miter joins and butt caps
 * override the shared root attributes, because the construction (R21) is half
 * of what tells the four marks apart at 16 px. The needle takes --accent and
 * the frame --ink-2, both read LIVE, so the mark is theme-tinted with no
 * baked hex (spec section 7). The hexagon and the hidden-H reading are
 * Starship's alone across the programme (R9/R22). */
function starshipMark(attention: boolean): string {
  const accent = token('--accent', '#34C3D6');
  const frame = token('--ink-2', '#8EA3BD');
  return (
    OPEN +
    `<g stroke-width="1.9" stroke-linecap="butt" stroke-linejoin="miter">` +
    `<path d="M12 3 19.79 7.5 19.79 16.5 12 21 4.21 16.5 4.21 7.5Z" stroke="${frame}"/>` +
    `<path d="M12 3.4V20.6" stroke="${accent}" stroke-width="2.2"/>` +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** MAINFRAME '84 — THE BLOCK ALONE, at favicon detail: a filled rectangle at
 * a 2:3 ratio with the baseline rule under it. The caret's blades are
 * dropped below 20px because they smear, so the favicon carries SILHOUETTE
 * and not detail (R22: solid block, distinguishable at 16px from the other
 * six marks). Filled, never stroked — the whole style's icon construction is
 * cells, and the mark may not be the one exception. Painted from the live
 * `--accent`, so it is theme-tinted across all four phosphor slots with no
 * baked hex (spec §7).
 *
 * IT NEVER BLINKS, and that is deliberate rather than an omission: the block
 * cursor is this style's one animation, and a favicon cannot honour
 * `prefers-reduced-motion`. */
function mainframeMark(attention: boolean): string {
  const accent = token('--accent', '#46E077');
  return (
    OPEN +
    `<g fill="${accent}" stroke="none" shape-rendering="crispEdges">` +
    '<rect x="7" y="3" width="10" height="15"/>' +
    '<rect x="3" y="20" width="18" height="2"/>' +
    '</g>' +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** STUDIO — the level, at favicon detail: the two unequal bars alone, cropped
 * tight, no frame, weights bumped for 16px (spec §7). The silhouette is "two
 * unequal bars", distinct at favicon size from circle / hexagon / squared
 * roundel / cross-with-a-dot / solid block / half-disc (R22).
 *
 * The BARS bind to `--accent` and nothing else does, which is the whole
 * STUDIO contract visible in miniature: one shared ground, only the primary
 * varies. Painted from the live token, so it re-tints on a theme switch with
 * no baked hex and no second file. */
function studioMark(attention: boolean): string {
  const accent = token('--accent', '#5AA9F0');
  return (
    OPEN +
    `<g stroke="${accent}" stroke-width="4.4">` +
    '<path d="M3.4 8.6h17.2"/><path d="M3.4 15.4h9.6"/>' +
    '</g>' +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** DESTROYER — the bow wave: a stencil chevron inside a squared roundel (the
 * hull-number plaque, R22). At favicon scale the roundel disappears below
 * 20 px, so the tab mark is the CHEVRON ALONE, filled solid — still
 * identifiable as DESTROYER at 16 px, which is the whole test. It is painted
 * in the live `--accent`, so it re-tints per theme with zero per-theme art:
 * running-light green under Blackout and Haze Gray, the pale filament of a
 * battle lantern under Battle Lanterns (spec §7). */
function destroyerMark(attention: boolean): string {
  const accent = token('--accent', '#4FB06B');
  const ink3 = token('--ink-3', '#717D8A');
  return (
    OPEN +
    // the plaque, hinted at with a bridged hairline so the mark still reads as
    // cut steel at 32 px and simply drops out at 16
    `<path d="M13.4 2.6H16l5.4 5.4v8L16 21.4H8L2.6 16V8L8 2.6h2.6" stroke="${ink3}" stroke-width="1.4" stroke-linecap="butt" stroke-linejoin="miter" opacity=".55"/>` +
    `<path d="M5.6 16.4 12 6.6l6.4 9.8Z" fill="${accent}" stroke="${accent}" stroke-width="1.6" stroke-linejoin="miter"/>` +
    (attention ? badge() : '') +
    '</svg>'
  );
}

/** Per-style mark painters. A style with no entry falls through to the
 * identity mark — a missing brand asset degrades to Blueprint's, NAMED here,
 * never to a blank tab icon. */
const MARKS: Record<string, (attention: boolean) => string> = {
  blueprint: blueprintMark,
  glass: glassMark,
  synthwave: synthwaveMark,
  quarterdeck: quarterdeckMark,
  starship: starshipMark,
  studio: studioMark,
  mainframe: mainframeMark,
  destroyer: destroyerMark,
};

/** Style ids that ship their own mark, so "which styles have brand assets" is
 * a counted fact rather than a comment.
 *
 * The counter is `scripts/style-assets-check.mjs` (the style asset-bundle
 * gate), which esbuild-bundles this module and CALLS this function — it does
 * not re-type the MARKS table, so the gate cannot agree with this comment
 * while disagreeing with the code. Until 2026-09-07 the comment here named
 * the icons-check set arm as its reader; repo-wide consumers were 0, which is
 * exactly the kind of claim this framework's gates exist to refuse. */
export function styleIdsWithBrandMark(): string[] {
  return Object.keys(MARKS);
}

/**
 * The href for `<link id="favicon">` under the painted style.
 *
 * Blueprint with nothing pending resolves to the static '/favicon.svg' — the
 * exact string app.ts wrote before PR-5, so the identity claim on this seam
 * is provable by absence rather than by inspection.
 */
export function brandFaviconHref(attention: boolean): string {
  const style = currentStyle();
  if (style === 'blueprint' && !attention) return '/favicon.svg';
  const paint = MARKS[style] ?? blueprintMark;
  return dataUri(paint(attention));
}

let attentionOn = false;

/** Repaint the tab icon from the remembered attention state. Called on every
 * style/theme repaint, so the mark follows a style switch immediately
 * instead of at the next attention flip. */
export function refreshBrandFavicon(): void {
  // Guarded, not assumed: the console's own suites drive appearance.apply()
  // against a stub document with no getElementById (measured 2026-09-08,
  // home-chrome + appearance-hydrate went red on exactly this line).
  if (typeof document === 'undefined' || typeof document.getElementById !== 'function') return;
  const link = document.getElementById('favicon') as HTMLLinkElement | null;
  if (!link) return;
  link.href = brandFaviconHref(attentionOn);
  // The one writer also SAYS which style it wrote for, so a real-browser
  // probe can distinguish "the tab mark follows the style" against the mere
  // presence of some data URI, without decoding the SVG
  // (e2e/style-parity-probe.mjs, brand arm). Prose in this module must not
  // spell an import specifier: ensure-style-runtime.mjs walks every
  // module-specifier token in this file to build the console's closure.
  link.setAttribute('data-brand-style', currentStyle());
}

// ---------------------------------------------------------------------
// THE STANDALONE MARKS — logo + wordmark per style, mounted into hooks
// ---------------------------------------------------------------------
//
// Owner ruling 2026-09-08: "For each style, we're meant to use different
// favicon and svg wordmark & logo that matches the style and theme."
// MEASURED before this block: public/logo-<id>.svg existed for 7 styles and
// application code read NONE of them (the in-app mark was the sprite's
// `icon('helm')`, re-cut per set — a tab-icon-sized glyph, not the logo),
// there was no wordmark anywhere, and the Bridge home spelled "The Bridge"
// in a <span> whose face was whatever --font-display resolved to.
//
// A host declares a HOOK and renders its Blueprint content inside it:
//   <span data-brand-mark>{sprite helm}</span>
//   <span data-brand-wordmark="bridge">The Bridge</span>   (or ="helm")
// mountBrandMarks() then fetches the painted style's standalone asset
// (`/logo-<id>.svg`, `/wordmark-<id>.svg`, `/wordmark-helm-<id>.svg` — one
// same-origin GET per asset per page, cached) and INLINES it, so the SVG's
// `currentColor` takes the host's ink exactly as the sprite does. Inlining,
// not <img>: an <img> cannot inherit colour, and a CSS mask would flatten
// GLASS's three-hue spectral sliver to one.
//
// ADDITIVE BY CONSTRUCTION. Under the identity style nothing is fetched and
// the hook keeps the content the host rendered, byte for byte (restored if a
// styled paint had replaced it) — style:identity's zero-diff claim holds on
// the DOM as well as the material. A missing asset (404, not an <svg>)
// degrades to that same content and is NAMED in the returned report, never a
// blank slot.
export function brandLogoHref(style: string = currentStyle()): string {
  return `/logo-${style}.svg`;
}
export function brandWordmarkHref(name: 'bridge' | 'helm', style: string = currentStyle()): string {
  return name === 'helm' ? `/wordmark-helm-${style}.svg` : `/wordmark-${style}.svg`;
}

const assetCache = new Map<string, Promise<string | null>>();
function fetchAsset(href: string): Promise<string | null> {
  let p = assetCache.get(href);
  if (!p) {
    p = fetch(href)
      .then(async (r) => {
        if (!r.ok) return null;
        const t = await r.text();
        return /^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/.test(t) ? t : null;
      })
      .catch(() => null);
    assetCache.set(href, p);
  }
  return p;
}
/** What each hook rendered on its own (its Blueprint content), captured the
 * first time it is seen so the identity style can be restored exactly. */
const identityContent = new WeakMap<Element, string>();
let mountSeq = 0;

export interface BrandMountReport {
  style: string;
  hooks: number;
  mounted: number;
  identity: number;
  missing: string[];
}

/** Converge every `[data-brand-mark]` / `[data-brand-wordmark]` hook under
 * `root` on the painted style. Safe to call on every repaint and after any
 * host render: a superseded call (a newer paint started before its fetch
 * returned) writes nothing. */
export async function mountBrandMarks(root: ParentNode = document): Promise<BrandMountReport> {
  const style = currentStyle();
  const seq = ++mountSeq;
  const q = (root as { querySelectorAll?: unknown }).querySelectorAll;
  const hooks = typeof q === 'function' ? [...root.querySelectorAll<HTMLElement>('[data-brand-mark], [data-brand-wordmark]')] : [];
  const report: BrandMountReport = { style, hooks: hooks.length, mounted: 0, identity: 0, missing: [] };
  await Promise.all(
    hooks.map(async (el) => {
      if (!identityContent.has(el)) identityContent.set(el, el.innerHTML);
      const restore = (): void => {
        if (el.hasAttribute('data-brand-src')) {
          el.innerHTML = identityContent.get(el) ?? '';
          el.removeAttribute('data-brand-src');
        }
      };
      if (style === 'blueprint') {
        restore();
        report.identity++;
        return;
      }
      const href = el.hasAttribute('data-brand-wordmark')
        ? brandWordmarkHref(el.getAttribute('data-brand-wordmark') === 'helm' ? 'helm' : 'bridge', style)
        : brandLogoHref(style);
      if (el.getAttribute('data-brand-src') === href) {
        report.mounted++;
        return;
      }
      // The hook this mount found, captured BEFORE the await (#4125).
      const hookAttr = el.hasAttribute('data-brand-wordmark') ? 'data-brand-wordmark' : 'data-brand-mark';
      const hookValue = el.getAttribute(hookAttr);
      const svg = await fetchAsset(href);
      if (seq !== mountSeq) return;
      // An awaited fetch ALWAYS resumes after the synchronous code that
      // followed this call, even when it resolves immediately. On a
      // console-backed Helm that code is applyReleaseChrome (app.ts, right
      // after mountRail): it takes this element over as the release block and
      // releases the hook. Writing now would erase the release name, switcher,
      // Start and completion banner (measured before this check: present in 8
      // of 64 completed cells, Blueprint only, with the logo answered at zero
      // latency too). So write NOTHING, neither the logo nor a restore, unless
      // the element is still the hook, with the same value, this mount found.
      if (el.isConnected === false || el.getAttribute(hookAttr) !== hookValue) return;
      if (!svg) {
        restore();
        report.missing.push(href);
        return;
      }
      el.innerHTML = svg;
      el.setAttribute('data-brand-src', href);
      report.mounted++;
    }),
  );
  return report;
}

/** THE per-repaint brand refresh: the tab icon and every mounted mark follow
 * the painted style together. Hosts call this from their one apply path
 * (style-runtime.ts onStylePainted; the console's style-substrate; the
 * portal's onPortalStylePainted) and after rendering a hook. */
export function refreshBrand(root?: ParentNode): void {
  refreshBrandFavicon();
  if (typeof document !== 'undefined' && typeof fetch === 'function') void mountBrandMarks(root ?? document);
}

/** Set the attention state and repaint. The ONE entry point app.ts uses.
 *
 * It also STAMPS the state on <html> as `data-attn`, because the tab badge is
 * not the only surface that wants it: STARSHIP's fleet-intent strip takes the
 * hue of the loudest open intent, and "sessions needing attention + open
 * user-held escalations" — the count this function already receives — IS the
 * "something needs staffing or a gate is waiting" rung. Stamping it here
 * rather than computing it a second time somewhere else is the same
 * one-writer discipline the favicon has.
 *
 * ADDITIVE: no Blueprint rule reads the attribute, and a style that does not
 * ask for it is unaffected. */
export function setBrandAttention(on: boolean): void {
  attentionOn = on;
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-attn', on ? '1' : '0');
  }
  refreshBrandFavicon();
}

